import React, { useContext, Fragment } from "react";

import VerticalKebabIcon from "@material-ui/icons/MoreVert";
import groupBy from "lodash/groupBy";
import truncate from "lodash/truncate";

import { ActionablesContext } from "@contexts/ActionablesContext";
import { Table } from "@components/Table";
import { getLongDateFromDate } from "@utils/dateParser";
import { humanizeString } from "@utils/stringMinipulation";
import { Button } from "@components/Button";
import { Tooltip } from "@components/Tooltip";
import { CardActionButton } from "@components/layout/Card/CardActionButton";
import { usePopper, Popper } from "@components/Popper";
import useFeatureConfig, { FeatureNames } from "@hooks/useFeatureConfig";
import { useCreateInvestmentProposal } from "@/containers/Scenario/components/CreateInvestmentProposal";

import { useQuickReportAssetIssue } from "../../components/QuickReportAssetIssueModal";
import { AssetsContext } from "../../context";
import { formatValuesForDisplay } from "../../utils";
import { parseNumber } from "@utils/numberConversions";

import "react-tabs/style/react-tabs.scss";

export const CurrentAssetsTable = () => {
  return (
    <>
      <Table assets>
        <table>
          <TableHead />
          <tbody>
            <TableContent />
          </tbody>
        </table>
      </Table>
    </>
  );
};

const TableHead = () => {
  return (
    <thead>
      <tr>
        <th>Assets</th>
        <th>Current Value</th>
        <th>Contributions (p/m)</th>
        <th className="hidden ml:table-cell">Income (p/m)</th>
        <th className="hidden xs:table-cell">Targeted Return rate (%)</th>
        <th className="hidden xs:table-cell">Contract number</th>
        <th className="hidden sm:table-cell">Last Imported</th>
        <th className="hidden sm:table-cell">Last Edit</th>
        <th className="hidden sm:table-cell" />
      </tr>
    </thead>
  );
};

const TableContent = () => {
  const { state } = useContext(AssetsContext);
  const {
    assets,
    planType,
    riskProfile,
    tenant: { slug: tenantSlug },
    userDetails: { client_id, profile_id },
  } = state;
  const risk_profile_id = riskProfile?.id;
  const [
    { isEnabled: isQuickReportIssuesEnabled },
    { isEnabled: isInvestmentProposalsEnabled },
  ] = useFeatureConfig(
    FeatureNames.CUSTOMER_SUPPORT_TICKET__QUICK_REPORT_ISSUE,
    FeatureNames.INVESTMENT_PROPOSAL__CREATE
  );
  const quickReportIssue = useQuickReportAssetIssue({
    tenantSlug,
    client_id,
    profile_id,
  });
  const createInvestmentProposal = useCreateInvestmentProposal({
    dfmIntegrationSlug: "strategiq",
    tenantSlug,
    client_id,
    profile_id,
    risk_profile_id,
    assets,
    scenarioPlanType: planType,
  });

  let indexedAssets = assets.map((asset, index) => {
    asset["index"] = index;
    return asset;
  });
  let groupedAssets = groupBy(indexedAssets, "asset_type");

  return (
    <>
      {Object.keys(groupedAssets).map((assetType) => (
        <Fragment key={assetType}>
          <AssetHeader key={assetType} type={assetType} />
          {groupedAssets[assetType].map((asset) => (
            <AssetContent
              key={asset.id}
              asset={asset}
              index={asset.index}
              isQuickReportIssuesEnabled={isQuickReportIssuesEnabled()}
              showQuickReportModalForAsset={
                quickReportIssue.showQuickReportModalForAsset
              }
              isInvestmentProposalsEnabledForAsset={createInvestmentProposal.isEnabledForAsset(
                asset
              )}
              showCreateInvestmentProposalModal={
                createInvestmentProposal.showCreateInvestmentProposalModal
              }
            />
          ))}
        </Fragment>
      ))}

      {isQuickReportIssuesEnabled() && quickReportIssue.modal}
      {isInvestmentProposalsEnabled() && createInvestmentProposal.modal}
    </>
  );
};

const AssetHeader = ({ type }) => (
  <tr className="asset-type-row">
    <td>
      <h4 className="asset-type">{humanizeString(type)}</h4>
    </td>
    <td />
    <td />
    <td className="hidden ml:table-cell" />
    <td className="hidden xs:table-cell" />
    <td className="hidden xs:table-cell" />
    <td className="hidden sm:table-cell" />
    <td className="hidden sm:table-cell" />
    <td className="hidden sm:table-cell" />
  </tr>
);

const AssetContent = ({
  asset,
  isQuickReportIssuesEnabled,
  showQuickReportModalForAsset,
  isInvestmentProposalsEnabledForAsset,
  showCreateInvestmentProposalModal,
}) => {
  const { state, actions } = useContext(AssetsContext);
  const { readOnly, frozen } = useContext(ActionablesContext);
  const { supportedCurrencies, scenario } = state;
  const { deleteAsset, unlinkAsset, updateAsset } = actions;
  const anyDropdownActionsEnabled =
    isQuickReportIssuesEnabled || isInvestmentProposalsEnabledForAsset;

  const className =
    (asset.flagged_for_deletion ? "flagged-for-deletion " : "") +
    (asset.new ? "is-new" : "");

  const popper = usePopper({ uniqueId: asset.id });
  const showImportedDate =
    asset.grouping_identifier &&
    (asset.last_automatic_import_at || asset.price_date);

  return (
    <tr className={className}>
      <td style={{ display: "flex" }}>
        {asset.new && <div className="new-asset">new</div>}
        <Tooltip title={<><span>{asset.name}</span><br /><span>{asset.linked ? "Contract linked: automatic imports can update this asset." : "Disconnected: this asset is not linked to automatic imports."}</span></>}>
          <span>
            <span className={asset.linked ? "gci-icon-link" : "gci-icon-unlink"} aria-hidden="true" />
            <span>{truncate(asset.name, { length: 28 })}</span>
          </span>
        </Tooltip>
      </td>
      <td>
        {formatValuesForDisplay(
          asset.gross_value,
          asset.currency,
          supportedCurrencies
        )}
      </td>
      <td>
        {formatValuesForDisplay(
          asset.computedAttributes.totalMonthlyContribution,
          asset.currency,
          supportedCurrencies
        )}
      </td>
      <td className="hidden ml:table-cell">
        {formatValuesForDisplay(
          asset.computedAttributes.totalMonthlyIncome,
          asset.currency,
          supportedCurrencies
        )}
      </td>
      <td className="hidden xs:table-cell">
        {asset.interest_rate
          ? `${parseNumber(asset.interest_rate * 100).toFixed(2)}%`
          : ""}
      </td>
      <td className="hidden xs:table-cell">
        {asset.contract_number
          ? <Tooltip title={asset.contract_number}><span>{truncate(asset.contract_number, { length: 24 })}</span></Tooltip>
          : ""}
      </td>
      <td className="hidden sm:table-cell">
        {showImportedDate
          ? new Date(
              asset.last_automatic_import_at || asset.price_date
            ).toLocaleDateString()
          : "-"}
      </td>
      <td className="hidden sm:table-cell">
        {new Date(asset.updated_at).toLocaleDateString()}
      </td>
      <td className="hidden sm:table-cell">
        {!readOnly && scenario?.id && !frozen && (
          <>
            <section className="asset-actions">
              {asset.flagged_for_deletion && asset.unlinkModes?.includes("permanent") ? (
                <>
                  <CardActionButton
                    icon="trash"
                    label="Delete asset"
                    trackAs={"Disconnected Asset: Delete"}
                    className="text-link asset-action flagged-for-deletion"
                    onClick={deleteAsset(asset)}
                  />
                  <Tooltip title="Unlink asset">
                    <Button
                      aria-label="Unlink asset"
                      className="text-link asset-action asset-action--unlink flagged-for-deletion"
                      trackAs={"Disconnected Asset: Unlink"}
                      onClick={unlinkAsset(asset)}
                    >
                      <span className="gci-icon-unlink" aria-hidden="true" />
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <>
                  <CardActionButton
                    icon="trash"
                    label="Delete asset"
                    trackAs={"Asset List: Delete"}
                    className="text-link asset-action"
                    onClick={deleteAsset(asset)}
                  />
                  <CardActionButton
                    icon="pencil"
                    label="Update asset"
                    trackAs={"Asset List: Update"}
                    className="text-link asset-action"
                    onClick={updateAsset(asset)}
                  />
                  {(asset.flagged_for_deletion || asset.unlinked_at) && asset.unlinkModes?.includes("permanent") && <Tooltip title="Unlink asset"><Button aria-label="Unlink asset" className="text-link asset-action asset-action--unlink" onClick={unlinkAsset(asset)}><span className="gci-icon-unlink" aria-hidden="true" /></Button></Tooltip>}
                </>
              )}

              {anyDropdownActionsEnabled && (
                <Tooltip title="More asset actions">
                  <button
                    aria-describedby={popper.id}
                    onClick={popper.togglePopper}
                    className="asset-actions__dropdown-trigger"
                    aria-label="More asset actions"
                  >
                    <VerticalKebabIcon />
                  </button>
                </Tooltip>
              )}
            </section>

            {anyDropdownActionsEnabled && (
              <Popper
                id={popper.id}
                isOpen={popper.isOpen}
                anchorElement={popper.anchorElement}
                closePopper={popper.closePopper}
                variant="card"
              >
                {isQuickReportIssuesEnabled && (
                  <Button
                    textLink
                    className={
                      "asset-actions__dropdown-item asset-actions__dropdown-item--table"
                    }
                    onClick={() => showQuickReportModalForAsset(asset)}
                  >
                    Report an issue
                  </Button>
                )}
                {isInvestmentProposalsEnabledForAsset && (
                  <Button
                    textLink
                    className={
                      "asset-actions__dropdown-item asset-actions__dropdown-item--table"
                    }
                    onClick={() => showCreateInvestmentProposalModal(asset)}
                  >
                    Create investment proposal
                  </Button>
                )}
              </Popper>
            )}
          </>
        )}
      </td>
    </tr>
  );
};
